// Data seam: sphinx REST (/api/sphinx/chat-sessions).
//
// Decisions:
// - listCurrentHistory → REST list, mapped to `ChatHistorySession` rows under
//   `personal` (sphinx has no project-shared history; `project` is []).
// - getCurrentSession → REST get, mapped to `ChatHistoryDetailSession`. The
//   row's `json` is the playground transcript payload; it is exposed on the
//   extra `payload` field, and `messagesBlobUrl` points at the transcript
//   endpoint (`/chat-sessions/:id/messages`) — the fork's chat client reopens a
//   thread by fetching messages from that URL, so sphinx serves one instead of
//   the Convex blob store it no longer has.
// - renameCurrentSession → PATCH title. archiveCurrentSession → DELETE (the
//   schema has no archived state; archiving removes the row).
// - unarchive / share / unshare / pin / unpin / widget snapshots → no-ops
//   (no endpoint, no per-user state).
// - elicitations:respondToElicitation → hosted-only Convex rendezvous; resolves
//   `{ ok: false, reason: "unsupported" }` so callers show their fallback.
import type {
  ChatHistoryDetailResponse,
  ChatHistoryDetailSession,
  ChatHistorySession,
  ChatHistoryWidgetSnapshot,
  ResumeConfig,
} from "@/lib/apis/web/chat-history-api";
import { sphinxApi, type SphinxChatSession } from "@/lib/sphinx-api";
import {
  invalidateSphinxQueries,
  useSphinxQuery,
  withInvalidate,
} from "@/lib/use-sphinx-query";
import type { DataClient } from "./client";
import { DEFAULT_PROJECT_ID } from "./project";
import { OPERATOR_USER } from "./user";

const toMs = (iso: string | null | undefined) => {
  const ms = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(ms) ? ms : 0;
};

function previewFromPayload(json: unknown): string | null {
  if (!json || typeof json !== "object") return null;
  const messages = Array.isArray(json)
    ? json
    : (json as { messages?: unknown }).messages;
  if (!Array.isArray(messages)) return null;
  const first = messages.find(
    (m) => m && typeof m === "object" && (m as { role?: unknown }).role === "user"
  ) as { content?: unknown; parts?: unknown } | undefined;
  if (!first) return null;
  if (typeof first.content === "string") return first.content.slice(0, 200);
  if (Array.isArray(first.parts)) {
    const text = first.parts.find(
      (p) => p && typeof p === "object" && typeof (p as { text?: unknown }).text === "string"
    ) as { text: string } | undefined;
    return text?.text.slice(0, 200) ?? null;
  }
  return null;
}

export function toChatHistorySession(row: SphinxChatSession): ChatHistorySession {
  const payload =
    row.json && typeof row.json === "object" && !Array.isArray(row.json)
      ? (row.json as { messages?: unknown; modelId?: unknown; modelSource?: unknown })
      : undefined;
  const messages = Array.isArray(row.json) ? row.json : payload?.messages;
  return {
    _id: row.id,
    chatSessionId: row.id,
    projectId: DEFAULT_PROJECT_ID,
    customTitle: row.title ?? undefined,
    // The stored payload carries the turn's model (persistChatSession writes
    // it every turn). Without this mapping `detail.modelId` was always
    // undefined, so reopening a chat never restored its model/provider.
    ...(typeof payload?.modelId === "string" ? { modelId: payload.modelId } : {}),
    ...(typeof payload?.modelSource === "string"
      ? { modelSource: payload.modelSource }
      : {}),
    firstMessagePreview: row.title ?? previewFromPayload(row.json) ?? "Untitled session",
    status: "active",
    directVisibility: "private",
    messageCount: Array.isArray(messages) ? messages.length : 0,
    version: row.version ?? 0,
    startedAt: toMs(row.createdAt),
    lastActivityAt: toMs(row.updatedAt),
    userId: OPERATOR_USER._id,
    isPinned: false,
    manualUnread: false,
    isUnread: false,
  };
}

export type SphinxChatHistoryDetail = ChatHistoryDetailSession & {
  payload: unknown;
  /** A turn on this session started and has not persisted its reply yet. */
  turnInFlight: boolean;
  /** The Playground client lineup this chat was held with, when recorded. */
  playgroundLineup: string[] | null;
};

/**
 * Consider an in-flight turn stale after this long: a crashed turn never
 * clears its flag, and a reopened thread must not spin forever on it.
 */
const TURN_IN_FLIGHT_STALE_MS = 10 * 60_000;

function isTurnInFlight(row: SphinxChatSession): boolean {
  const payload = row.json as { turnInFlight?: unknown } | null | undefined;
  if (!payload || payload.turnInFlight !== true) return false;
  return Date.now() - toMs(row.updatedAt) < TURN_IN_FLIGHT_STALE_MS;
}

/**
 * Where the stored transcript is served from — see the header.
 *
 * The version rides along as a cache-buster. The fork minted a NEW immutable
 * blob URL per persist, and two consumers lean on that: the prefetch blob
 * cache keys on the URL, and the surfaces' history content signature IS the
 * URL — a stable URL made every reactive update read as "already applied",
 * so a thread reopened mid-turn never swapped the finished reply in. Every
 * write bumps `version`, so versioning the URL restores the fork's
 * URL-identifies-content contract. The route ignores the query param.
 */
export function chatSessionMessagesUrl(id: string, version?: number): string {
  const base = `/api/sphinx/chat-sessions/${encodeURIComponent(id)}/messages`;
  return typeof version === "number" ? `${base}?v=${version}` : base;
}

function resumeConfigFromPayload(json: unknown): ResumeConfig | undefined {
  if (!json || typeof json !== "object") return undefined;
  const resumeConfig = (json as { resumeConfig?: unknown }).resumeConfig;
  return resumeConfig && typeof resumeConfig === "object"
    ? (resumeConfig as ResumeConfig)
    : undefined;
}

export function toChatHistoryDetail(
  row: SphinxChatSession & { json: unknown }
): SphinxChatHistoryDetail {
  const resumeConfig = resumeConfigFromPayload(row.json);
  return {
    ...toChatHistorySession(row),
    messagesBlobUrl: chatSessionMessagesUrl(row.id, row.version ?? 0),
    ...(resumeConfig ? { resumeConfig } : {}),
    payload: row.json,
    turnInFlight: isTurnInFlight(row),
    playgroundLineup: Array.isArray(row.lineup)
      ? row.lineup.filter((id): id is string => typeof id === "string")
      : null,
  };
}

/**
 * `directChatHistory:getCurrentSession` as a one-shot — the shape the fork's
 * `/api/web/chat-history/detail` returned, which is what the chat client and
 * the promote-to-eval dialog read. sphinx serves it from Postgres; there is no
 * hosted web API behind that path.
 */
export async function fetchChatHistoryDetail(
  chatSessionId: string
): Promise<ChatHistoryDetailResponse> {
  const row = await sphinxApi.chatSessions.get(chatSessionId);
  return { ok: true, session: toChatHistoryDetail(row) };
}

const NO_SNAPSHOTS: ChatHistoryWidgetSnapshot[] = [];

// ---- directChatHistory:* queries ---------------------------------------------

/** The rail keeps itself honest: a slow ambient poll, faster while any listed
 * session changed in the last two minutes (an in-flight or just-finished turn
 * bumps `updatedAt` server-side). Invalidation is still the fast path — this
 * poll is the net under every signal a tab can miss: a turn started in another
 * tab, a dropped SSE part, a persist that raced the invalidation
 * ("chats only appear after a hard refresh", 2026-08-25). */
const LIST_POLL_IDLE_MS = 20_000;
const LIST_POLL_ACTIVE_MS = 3_000;
const LIST_RECENT_WINDOW_MS = 2 * 60_000;

/** directChatHistory:listCurrentHistory */
export function useCurrentChatHistory(args: Record<string, unknown> | "skip") {
  return useSphinxQuery(
    args === "skip" ? null : "chatSessions:list",
    fetchChatHistoryList,
    {
      pollMs: (data) =>
        data?.personal?.some(
          (s) => Date.now() - s.lastActivityAt < LIST_RECENT_WINDOW_MS
        )
          ? LIST_POLL_ACTIVE_MS
          : LIST_POLL_IDLE_MS,
    }
  ) as { personal: ChatHistorySession[]; project: ChatHistorySession[] } | undefined;
}

/** How often a reopened thread polls a still-in-flight session. */
const TURN_IN_FLIGHT_POLL_MS = 2_000;

/**
 * directChatHistory:getCurrentSession
 *
 * Polls while the session's turn is in flight, so a thread reopened mid-turn
 * picks the finished reply up on its own: the version bump this poll observes
 * is what drives the surfaces' reactive transcript reload.
 */
export function useCurrentChatSession(
  args: { sessionId: string; projectId?: string } | "skip"
) {
  const id = args === "skip" ? null : args.sessionId;
  return useSphinxQuery(
    id ? `chatSessions:get:${id}` : null,
    async () => toChatHistoryDetail(await sphinxApi.chatSessions.get(id!)),
    {
      pollMs: (detail) =>
        detail?.turnInFlight ? TURN_IN_FLIGHT_POLL_MS : null,
    }
  ) as SphinxChatHistoryDetail | null | undefined;
}

/** directChatHistory:getCurrentSessionWidgetSnapshots */
export function useCurrentChatSessionWidgetSnapshots(
  args: { sessionId: string } | "skip"
) {
  return args === "skip" ? undefined : NO_SNAPSHOTS;
}

// ---- directChatHistory:* mutations -------------------------------------------

const renameCurrentSession = withInvalidate(
  async (args: { sessionId: string; customTitle: string }) => {
    await sphinxApi.chatSessions.patch(args.sessionId, {
      title: args.customTitle.trim() || null,
    });
  }
);

const archiveCurrentSession = withInvalidate(async (args: { sessionId: string }) => {
  await sphinxApi.chatSessions.delete(args.sessionId);
});

const noop = async (_args?: unknown): Promise<void> => {};

/**
 * The one-shot twin of the mutations below, for the callers that reached the
 * fork's `POST /api/web/chat-history/action` directly instead of through a
 * hook. sphinx has no such route, so the action is resolved here: `rename` and
 * `archive` are the only two with a Postgres effect. `mark-read` and the
 * sharing/pinning actions have no per-user state to write (`isUnread` is always
 * false, `directVisibility` always "private"), so they resolve as no-ops rather
 * than 404ing silently.
 */
export async function runChatSessionAction(
  action: string,
  sessionId: string,
  params?: Record<string, unknown>
): Promise<void> {
  switch (action) {
    case "rename":
      await renameCurrentSession({
        sessionId,
        customTitle: String(params?.customTitle ?? ""),
      });
      return;
    case "archive":
      await archiveCurrentSession({ sessionId });
      return;
    default:
      return;
  }
}

/**
 * Re-fetch the rail's list (and every other mounted sphinx query).
 *
 * The fork's rail was a reactive Convex query, so a first-turn persist showed
 * up on its own. The REST seam re-fetches only on invalidation, and only
 * mutations invalidated — a freshly created session stayed off the rail until
 * an unrelated rename/archive or a reload. The post-stream refresh calls this.
 */
export function refreshChatHistoryList(): void {
  invalidateSphinxQueries();
}

/** The list behind `directChatHistory:listCurrentHistory`, as a one-shot. */
export async function fetchChatHistoryList(): Promise<{
  personal: ChatHistorySession[];
  project: ChatHistorySession[];
}> {
  return {
    personal: (await sphinxApi.chatSessions.list()).map(toChatHistorySession),
    project: [],
  };
}

/** directChatHistory:renameCurrentSession */
export function useRenameCurrentSessionMutation() {
  return renameCurrentSession;
}

/** directChatHistory:archiveCurrentSession (deletes — see header) */
export function useArchiveCurrentSessionMutation() {
  return archiveCurrentSession;
}

/** directChatHistory:unarchiveCurrentSession */
export function useUnarchiveCurrentSessionMutation() {
  return noop;
}

/** directChatHistory:shareCurrentSession */
export function useShareCurrentSessionMutation() {
  return noop;
}

/** directChatHistory:unshareCurrentSession */
export function useUnshareCurrentSessionMutation() {
  return noop;
}

/** directChatHistory:pinCurrentSession */
export function usePinCurrentSessionMutation() {
  return noop;
}

/** directChatHistory:unpinCurrentSession */
export function useUnpinCurrentSessionMutation() {
  return noop;
}

// ---- elicitations:* ----------------------------------------------------------

/** elicitations:respondToElicitation (one-shot) */
export async function respondToElicitation(
  _client: DataClient,
  _args: Record<string, unknown>
): Promise<{ ok: false; reason: string }> {
  return { ok: false, reason: "unsupported" };
}
